import { createFileRoute } from '@tanstack/react-router';
import { fetchOrderTags, fetchPurchaseOrderById } from '@/api/orders';
import { PurchaseOrderOverviewTab } from '@/app/(client)/orders/-components/PurchaseOrderOverviewTab';
import {
  buyerCompaniesQueryOptions,
  useBuyerCompaniesDropdown,
} from '@/app/(client)/orders/-hooks/useBuyerCompaniesDropdown';
import {
  orderTypesQueryOptions,
  useOrderTypes,
} from '@/app/(client)/orders/-hooks/useOrderTypes';
import {
  purchaseOrderDetailKeys,
  usePurchaseOrderDetail,
} from '@/app/(client)/orders/-hooks/usePurchaseOrderDetail';
import {
  attachmentTemplatesQueryOptions,
  useAttachmentTemplatesForImport,
} from '@/hooks/useAttachmentTemplatesForImport';
import { useFacilityProcesses } from '@/hooks/useFacilityProcesses';
import { groupedFacilityProcessOptionsQueryOptions } from '@/hooks/useGroupedFacilityProcessOptions';
import { useOrderTags } from '@/hooks/useOrderTags';
import { queryClient } from '@/lib/utils/queryClient';

export const Route = createFileRoute(
  '/(client)/orders/(pages)/(detail)/purchase-orders/$poId/overview',
)({
  loader: ({ params }) =>
    Promise.all([
      queryClient.ensureQueryData({
        queryKey: purchaseOrderDetailKeys.detail(params.poId),
        queryFn: () =>
          fetchPurchaseOrderById(params.poId).then(res => res.data),
      }),
      queryClient.ensureQueryData({
        queryKey: ['cascade-tracing', 'order-tags'],
        queryFn: () => fetchOrderTags().then(r => r.data),
      }),
      queryClient.ensureQueryData(orderTypesQueryOptions),
      queryClient.ensureQueryData(buyerCompaniesQueryOptions),
      queryClient.ensureQueryData(attachmentTemplatesQueryOptions),
      queryClient.ensureQueryData(groupedFacilityProcessOptionsQueryOptions),
    ]),
  component: PurchaseOrderOverviewRoute,
});

function PurchaseOrderOverviewRoute() {
  const { poId } = Route.useParams();
  const { data: order } = usePurchaseOrderDetail(poId);
  const { data: orderTags } = useOrderTags();
  const { data: orderTypes } = useOrderTypes();
  const { data: buyerCompaniesData } = useBuyerCompaniesDropdown();
  const { data: templatesData } = useAttachmentTemplatesForImport();
  const { processes: fpProcesses } = useFacilityProcesses();

  if (!order) return null;

  return (
    <PurchaseOrderOverviewTab
      order={order}
      orderTags={orderTags ?? []}
      orderTypes={orderTypes ?? []}
      buyerCompanies={buyerCompaniesData?.data ?? []}
      facilityProcessOptions={fpProcesses ?? []}
      attachmentTemplates={templatesData?.data ?? []}
    />
  );
}
